import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { SelfOwnedWidget, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import type { WidgetContainerChannelSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerTabSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';

export type PlaceholderWidgetEventBus = EventBus<
    EventBusSignals<WidgetContainerChannelSliceEventBus> & EventBusSignals<WidgetContainerTabSliceEventBus>,
    EventBusEvents<WidgetContainerChannelSliceEventBus> & EventBusEvents<WidgetContainerTabSliceEventBus>
>;

export interface PlaceholderWidgetModelInit {
    container: WidgetContainerController;
    tabId: WidgetId;
    widgetType: WidgetType;
    /** The tab's persisted state blob, held verbatim for serialization round-trips and the eventual real instantiation. */
    persistedState: unknown;
    logger: Logger;
}

/**
 * Permission-gated stand-in occupying a self-owned widget tab (ADR 0008).
 *
 * When the user is not entitled to a widget type, the Container instantiates
 * this instead of the real model: no data fetches run, the persisted state is
 * preserved verbatim through `toJSON`, and the standard container slices give
 * the header a real event bus (tabs, maximize, delete) like any other widget.
 * When entitlement is granted, the Container swaps this instance for a real
 * one constructed from {@link PlaceholderWidgetModel.persistedState}.
 */
export class PlaceholderWidgetModel implements SelfOwnedWidget<unknown>, Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #dirty = signal(false);
    readonly #persistedState: unknown;

    readonly widgetType: WidgetType;
    readonly eventBus: PlaceholderWidgetEventBus;
    readonly headerElements: WidgetHeaderElement[];

    constructor(init: PlaceholderWidgetModelInit) {
        this.widgetType = init.widgetType;
        this.#persistedState = init.persistedState;

        const channelSlice = this.#disposableStack.use(
            new WidgetContainerChannelSlice({ container: init.container, tabId: init.tabId }),
        );
        const widgetTabSlice = this.#disposableStack.use(
            new WidgetContainerTabSlice({ container: init.container, tabId: init.tabId, logger: init.logger }),
        );

        this.eventBus = MergedEventBus.from<WidgetContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        ).merge<WidgetContainerTabSliceEventBus>(widgetTabSlice.eventBus);

        // The widget itself is inaccessible, so the channel picker is omitted;
        // tab/window chrome stays fully functional.
        this.headerElements = COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#dirty;
    }

    get persistedState(): unknown {
        return this.#persistedState;
    }

    toJSON(): unknown {
        return this.#persistedState;
    }

    markClean(): void {
        // Nothing to clean: a placeholder never mutates its held state.
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
